import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { Category } from 'components/ClubMemberManagement/types';
import { useClubSlug } from 'components/Clubs/hooks';
import { FormInput } from 'components/Form/next';
import FormErrorMessage from 'components/Form/next/FormErrorMessage';
import { Option } from 'components/Form/next/FormSelect';
import { FormInputProps } from 'components/Form/next/props';
import Joi from 'joi';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import useSWR from 'swr';

export const fellowRulesValidationRules = Joi.array()
    .items(
        Joi.object({
            guest_count: Joi.number().min(1).required().label('Anzahl Mitspieler'),
            for_category: Joi.number().min(0).allow('', null).label('Kategorie'),
            max_slots: Joi.number().allow(null, '').max(96).label('Maximale Buchungsdauer'),
            price: Joi.number().allow(null, '').max(1000).label('Aufpreis'),
            taxes: Joi.number().allow(null, '').max(100).min(0).label('MwSt')
        })
    )
    .unique((a, b) => a.guest_count === b.guest_count && a.for_category === b.for_category)
    .messages({ 'array.unique': '***Die Anzahl der Gäste darf nur einmal vorkommen.' })
    .label('Gastregeln');

interface Props extends FormInputProps {
    clubSlug?: string;
    label?: string;
}

export const FellowRulesFieldArray = ({ name, clubSlug: _clubSlug, label }: Props) => {
    const clubSlug = useClubSlug(_clubSlug);
    const { control } = useFormContext();
    const { append, fields, remove } = useFieldArray({ name, control });
    const { data: categoryData } = useSWR<{ data: Category[] }>(`club-member/${clubSlug}/category`);

    const options = useMemo(
        (): Option[] => [
            { key: '', label: 'egal' },
            ...(categoryData?.data || []).map((cat) => ({
                key: cat.id,
                label: cat.title
            }))
        ],
        [categoryData]
    );

    return (
        <div>
            <Button className="tw-mt-4 tw-w-full" size="sm" onPress={() => append({})}>
                {label || 'Mitspieler Regel hinzufügen'}
            </Button>
            {fields.map((field, index) => (
                <div
                    className="tw-my-6 tw-grid tw-grid-cols-[repeat(3,auto)] tw-items-baseline tw-gap-x-4 tw-rounded-md tw-border tw-border-solid tw-border-accents-4 tw-p-6"
                    key={field.id}
                >
                    <FormInput
                        type="number"
                        label="Anzahl Mitspieler"
                        contentLeft="Ab"
                        name={`${name}.${index}.guest_count`}
                    />
                    <FormInput.Select name={`${name}.${index}.for_category`} label="der Kategorie" options={options} />
                    <FormInput
                        type="number"
                        label="Maximale Buchungsdauer in Slots"
                        helperText="Ein Slot entspricht dabei 15 Minuten"
                        placeholder="unbegrenzt"
                        name={`${name}.${index}.max_slots`}
                    />
                    <FormInput
                        type="number"
                        step={0.01}
                        label="Aufpreis"
                        placeholder="keine Veränderung"
                        name={`${name}.${index}.price`}
                        contentRight="€"
                    />
                    <FormInput
                        type="number"
                        step={0.01}
                        label="MwSt"
                        placeholder="19"
                        name={`${name}.${index}.taxes`}
                        contentRight="%"
                    />
                    <Button size="xs" onPress={() => remove(index)} color="error">
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
            ))}
            <FormErrorMessage name={name} />
        </div>
    );
};
